import clsx from 'clsx';
import { FocusEvent, forwardRef, useRef } from 'react';

import IconButton from 'components/icon-button';
import InputLabel from 'components/input-label';

import clear from 'assets/images/input-clear.svg';
import styles from './styles.module.scss';

export type InputProps = {
  className?: string;
  value?: string;
  maxChars?: number;
  onChange?: (value: string) => void;
  onClean?: (value: string) => void;
  icon?: string;
  clearButton?: boolean;
  error?: string;
  tall?: boolean;
  label?: React.ReactNode;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;

const TextInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    value,
    onChange,
    onClean,
    onFocus,
    onBlur,
    icon,
    clearButton,
    className,
    error,
    maxChars,
    readOnly,
    tall,
    label,
    ...rest
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      let val = event.target.value.trimStart();

      if (maxChars && maxChars > 0) {
        val = val.slice(0, maxChars);
      }

      onChange(val);
    }
  };

  const handleFocus = (value: FocusEvent<HTMLInputElement, Element>) => {
    onFocus && onFocus(value);
  };

  const handleBlur = (value: FocusEvent<HTMLInputElement, Element>) => {
    onBlur && onBlur(value);
  };

  return (
    <div className={className}>
      <div className={styles.container}>
        {label && <InputLabel>{label}</InputLabel>}

        <input
          ref={ref}
          type='text'
          value={value}
          readOnly={readOnly}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={clsx(
            styles.input,
            tall && styles.tallInput,
            icon && styles.inputWithIcon,
            error && styles.inputWithError,
          )}
          onKeyUp={(e) => {
            if (e.code === 'Enter' || e.keyCode === 13) {
              e.currentTarget.blur();
            }
          }}
          {...rest}
        />

        {icon && (
          <img
            className={styles.icon}
            alt=''
            src={icon}
            width={styles.inputIconSize}
            height={styles.inputIconSize}
          />
        )}

        {clearButton && (
          <IconButton
            className={styles.clearButton}
            icon={clear}
            alt='Очистить'
            iconWidth={styles.inputClearButtonSize}
            iconHeight={styles.inputClearButtonSize}
            onClick={() => {
              onChange?.('');
              onClean?.('');
            }}
          />
        )}
      </div>

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
});

TextInput.displayName = 'TextInput';

export default TextInput;
